import React, { useState, useEffect, useMemo } from 'react';
import { X, ExternalLink, Loader } from 'lucide-react';
import { devFilesApi, dynamicApi, appCfgApi } from '../../services/api';
import { useDbNavigate } from '../../context/DatabaseContext';
import FilterableDataGrid from './FilterableDataGrid';

// Map table names to route paths
const tableToRoute = (tableName) => {
  const mappings = {
    'Building': 'buildings',
    'Drawing': 'drawings',
    'Admin_Users': 'users',
    'Source': 'source',
    'Discipline': 'discipline',
    'Commissioning_Key': 'commissioning-key',
    'Drawing_Log': 'drawing-log',
    'IP_Address_SLN': 'ip-address-sln'
  };
  return mappings[tableName] || tableName.toLowerCase().replace(/_/g, '-');
};

/**
 * FKRecordModal - Modal popup to view a related record
 * Supports stacking when clicking FK links within the modal
 * Uses the same config-driven layout as DynamicModule
 */
const FKRecordModal = ({ isOpen, onClose, initialTableName, initialRecordId }) => {
  const navigate = useDbNavigate();

  // Stack of records: [{tableName, recordId}, ...]
  const [stack, setStack] = useState([]);

  // Current record state
  const [record, setRecord] = useState(null);
  const [moduleConfig, setModuleConfig] = useState(null);
  const [fieldMetadata, setFieldMetadata] = useState({});
  const [appSettings, setAppSettings] = useState({});
  const [loading, setLoading] = useState(true);

  // Related tables
  const [activeRelatedTab, setActiveRelatedTab] = useState(0);
  const [relatedData, setRelatedData] = useState({});
  const [relatedLoading, setRelatedLoading] = useState({}); // Track loading per table
  const [relatedFilters, setRelatedFilters] = useState({});
  const [relatedMetadata, setRelatedMetadata] = useState({});

  // Get current item from stack
  const current = stack[stack.length - 1];

  // Initialize stack when modal opens
  useEffect(() => {
    if (isOpen && initialTableName && initialRecordId) {
      setStack([{ tableName: initialTableName, recordId: initialRecordId }]);
    } else if (!isOpen) {
      setStack([]);
      setRecord(null);
      setModuleConfig(null);
      setRelatedData({});
    }
  }, [isOpen, initialTableName, initialRecordId]);

  // Load record when current changes
  useEffect(() => {
    if (!current) return;

    const loadRecord = async () => {
      setLoading(true);
      setRecord(null);
      setRelatedData({});
      setActiveRelatedTab(0);

      try {
        // Load config
        try {
          const configRes = await devFilesApi.getModuleConfig(current.tableName);
          setModuleConfig(configRes.data);
        } catch {
          setModuleConfig(null);
        }

        // Load field metadata
        try {
          const metadataRes = await appCfgApi.getFieldMetadata(current.tableName);
          const metadataMap = {};
          (metadataRes.data || []).forEach(m => {
            metadataMap[m.field_Name] = m;
          });
          setFieldMetadata(metadataMap);
        } catch {
          setFieldMetadata({});
        }

        // Load app settings
        try {
          const settingsRes = await appCfgApi.getDisplaySettings();
          const settingsMap = {};
          (settingsRes.data || []).forEach(s => {
            settingsMap[s.setting_Key] = s.setting_Value;
          });
          setAppSettings(settingsMap);
        } catch {
          setAppSettings({});
        }

        // Load single record by ID
        const dataRes = await dynamicApi.getById(current.tableName, current.recordId);
        if (dataRes.data) {
          setRecord(dataRes.data);
        }
      } catch (err) {
        console.error('Failed to load record:', err);
      } finally {
        setLoading(false);
      }
    };

    loadRecord();
  }, [current]);

  // Load related data when record loads - parallel loading with per-table loading state
  useEffect(() => {
    if (!record || !moduleConfig?.relatedTables?.length) return;

    // Set all tables to loading state
    const initialLoading = {};
    moduleConfig.relatedTables.forEach(t => {
      initialLoading[t.tableName] = true;
    });
    setRelatedLoading(initialLoading);

    // Load each table in parallel
    moduleConfig.relatedTables.forEach(async (relatedTable) => {
      try {
        // Load metadata
        let metadataMap = {};
        try {
          const metadataRes = await appCfgApi.getFieldMetadata(relatedTable.tableName);
          (metadataRes.data || []).forEach(m => {
            metadataMap[m.field_Name] = m;
          });
        } catch {
          // Metadata optional
        }
        setRelatedMetadata(prev => ({ ...prev, [relatedTable.tableName]: metadataMap }));

        // Load filtered data
        const response = await dynamicApi.getFiltered(
          relatedTable.tableName,
          relatedTable.fkField,
          record.id
        );
        let filteredRecords = response.data || [];

        // Sort if configured
        if (relatedTable.sortField) {
          const sortKey = relatedTable.sortField.charAt(0).toLowerCase() + relatedTable.sortField.slice(1);
          const direction = relatedTable.sortDirection === 'DESC' ? -1 : 1;
          filteredRecords.sort((a, b) => {
            const aVal = a[sortKey] ?? '';
            const bVal = b[sortKey] ?? '';
            if (aVal < bVal) return -1 * direction;
            if (aVal > bVal) return 1 * direction;
            return 0;
          });
        }

        // Update data and clear loading for this table
        setRelatedData(prev => ({ ...prev, [relatedTable.tableName]: filteredRecords }));
        setRelatedLoading(prev => ({ ...prev, [relatedTable.tableName]: false }));
      } catch (err) {
        console.error(`Failed to load related ${relatedTable.tableName}:`, err);
        setRelatedData(prev => ({ ...prev, [relatedTable.tableName]: [] }));
        setRelatedLoading(prev => ({ ...prev, [relatedTable.tableName]: false }));
      }
    });
  }, [record, moduleConfig?.relatedTables]);

  // Handle FK click - push to stack
  const handleFKClick = (tableName, recordId) => {
    if (stack.length >= 5) {
      // Limit stack depth to prevent absurdity
      console.warn('FK modal stack limit reached');
      return;
    }
    setStack(prev => [...prev, { tableName, recordId }]);
  };

  // Handle close/back
  const handleBack = () => {
    if (stack.length > 1) {
      setStack(prev => prev.slice(0, -1));
    } else {
      onClose();
    }
  };

  // Handle ESC key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        handleBack();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, stack.length]);

  // Handle go to full page
  const handleGoToPage = () => {
    if (current) {
      navigate(`/${tableToRoute(current.tableName)}`, {
        state: { selectedId: current.recordId }
      });
      onClose();
    }
  };

  // Build breadcrumb from stack
  const breadcrumb = stack.map((item, idx) => {
    const displayName = item.tableName.replace(/_/g, ' ');
    return displayName;
  });

  // Organize fields by row
  const fieldsByRow = useMemo(() => {
    if (!moduleConfig?.detailPanel?.fields) return {};
    const rows = {};
    moduleConfig.detailPanel.fields.forEach(field => {
      const row = field.row || 1;
      if (!rows[row]) rows[row] = [];
      rows[row].push(field);
    });
    Object.keys(rows).forEach(row => {
      rows[row].sort((a, b) => (a.order || 0) - (b.order || 0));
    });
    return rows;
  }, [moduleConfig]);

  // Get field value
  const getFieldValue = (fieldName) => {
    if (!record) return '';
    const key = fieldName.charAt(0).toLowerCase() + fieldName.slice(1);
    return record[key] ?? '';
  };

  // Get FK display value
  const getFKDisplayValue = (fieldName) => {
    if (!record) return '';
    const baseKey = fieldName.charAt(0).toLowerCase() + fieldName.slice(1);
    const displayKey = baseKey + '_Display';
    if (record[displayKey] !== undefined) {
      return record[displayKey];
    }
    return record[baseKey] ?? '';
  };

  // Format date value
  const formatDateValue = (value, fieldName) => {
    if (!value) return '';
    const meta = fieldMetadata[fieldName];
    if (meta?.uI_Type === 'date') {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const year = date.getFullYear();
          return `${month}/${day}/${year}`;
        }
      } catch { /* return original */ }
    }
    return value;
  };

  // Render field
  const renderField = (field) => {
    if (field.fieldType === 'section') {
      return (
        <div key={field.fieldName} className="col-span-24">
          <div className="detail-section-header">{field.sectionTitle || field.label}</div>
        </div>
      );
    }

    if (field.fieldType === 'spacer') {
      return <div key={field.fieldName} className={`col-span-${field.colSpan || 4}`} />;
    }

    const value = getFieldValue(field.fieldName);
    const colSpan = field.colSpan || 8;
    const labelPos = field.labelPosition || 'top';
    const labelAlign = field.labelAlignment || 'left';
    const posClass = labelPos !== 'top' ? ` label-${labelPos}` : '';
    const alignClass = labelAlign !== 'left' ? ` label-align-${labelAlign}` : '';
    const groupClass = `form-group col-span-${colSpan}${posClass}${alignClass}`;

    // FK field with clickable link
    if (field.fkTable && field.fkClickable) {
      const displayValue = getFKDisplayValue(field.fieldName);
      const hasValue = value !== null && value !== undefined && value !== '' && value !== 0;

      if (hasValue) {
        return (
          <div key={field.fieldName} className={groupClass}>
            <label>{field.label}</label>
            <button
              type="button"
              className="form-input fk-link"
              onClick={() => handleFKClick(field.fkTable, value)}
            >
              {displayValue || value}
            </button>
          </div>
        );
      }
      return (
        <div key={field.fieldName} className={groupClass}>
          <label>{field.label}</label>
          <input type="text" value="" readOnly className="form-input" />
        </div>
      );
    }

    if (field.fieldType === 'checkbox') {
      return (
        <div key={field.fieldName} className={`form-group col-span-${colSpan}`}>
          <label className="checkbox-label">
            <input type="checkbox" checked={!!value} disabled />
            <span>{field.label}</span>
          </label>
        </div>
      );
    }

    if (field.fieldType === 'textarea') {
      return (
        <div key={field.fieldName} className={groupClass}>
          <label>{field.label}</label>
          <textarea value={value} readOnly className="form-input" rows={3} />
        </div>
      );
    }

    // Date field
    if (field.fieldType === 'date') {
      const displayValue = formatDateValue(value, field.fieldName);
      return (
        <div key={field.fieldName} className={groupClass}>
          <label>{field.label}</label>
          <input type="text" value={displayValue} readOnly className="form-input" />
        </div>
      );
    }

    // Default: text input
    return (
      <div key={field.fieldName} className={groupClass}>
        <label>{field.label}</label>
        <input
          type={field.fieldType === 'number' ? 'number' : 'text'}
          value={value}
          readOnly
          className="form-input"
        />
      </div>
    );
  };

  // Render related table grid
  const renderRelatedGrid = () => {
    if (!moduleConfig?.relatedTables?.length) return null;

    const activeTab = moduleConfig.relatedTables[activeRelatedTab];
    if (!activeTab) return null;

    // Show loading state
    if (relatedLoading[activeTab.tableName]) {
      return (
        <div className="related-grid-loading">
          <Loader size={24} className="spinning" />
          <span>Loading {activeTab.label}...</span>
        </div>
      );
    }

    const tabData = relatedData[activeTab.tableName] || [];
    const tableMeta = relatedMetadata[activeTab.tableName] || {};
    const sampleRow = tabData[0] || {};

    // Format date helper
    const formatDate = (value) => {
      if (!value) return '';
      try {
        const date = new Date(value);
        if (isNaN(date.getTime())) return value;
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        return `${month}/${day}/${year}`;
      } catch {
        return value;
      }
    };

    // Build columns
    const gridColumns = (activeTab.columns || []).map(col => {
      const meta = tableMeta[col.fieldName];
      const baseKey = col.fieldName.charAt(0).toLowerCase() + col.fieldName.slice(1);

      let key = baseKey;
      const displaySuffixKey = baseKey + '_Display';
      if (sampleRow[displaySuffixKey] !== undefined) {
        key = displaySuffixKey;
      } else if (meta?.fK_Display_Field) {
        const metaKey = meta.fK_Display_Field.charAt(0).toLowerCase() + meta.fK_Display_Field.slice(1);
        if (sampleRow[metaKey] !== undefined) {
          key = metaKey;
        }
      }

      return {
        key: key,
        label: col.label,
        width: meta?.default_Width || 150,
        uiType: meta?.uI_Type
      };
    });

    // Format data
    const formattedData = tabData.map(row => {
      const formatted = { ...row };
      gridColumns.forEach(col => {
        if (col.uiType === 'date' && formatted[col.key]) {
          formatted[col.key] = formatDate(formatted[col.key]);
        }
      });
      return formatted;
    });

    // Filters for this table
    const tableFilters = relatedFilters[activeTab.tableName] || {};
    const setTableFilters = (newFilters) => {
      if (typeof newFilters === 'function') {
        setRelatedFilters(prev => ({
          ...prev,
          [activeTab.tableName]: newFilters(prev[activeTab.tableName] || {})
        }));
      } else {
        setRelatedFilters(prev => ({
          ...prev,
          [activeTab.tableName]: newFilters
        }));
      }
    };

    return (
      <div className="related-table-grid-container">
        <FilterableDataGrid
          key={`${activeTab.tableName}-${formattedData.length}`}
          data={formattedData}
          columns={gridColumns}
          filters={tableFilters}
          setFilters={setTableFilters}
          allowColumnReorder={false}
          onRowDoubleClick={(row) => handleFKClick(activeTab.tableName, row.id)}
        />
      </div>
    );
  };

  if (!isOpen) return null;

  // Calculate offset for stacking effect
  const stackOffset = (stack.length - 1) * 15;

  return (
    <div className="fk-modal-overlay" onClick={handleBack}>
      <div
        className="fk-modal"
        style={{
          transform: `translate(${stackOffset}px, ${stackOffset}px)`
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="fk-modal-header">
          <div className="fk-modal-title">
            <span className="fk-modal-breadcrumb">
              {breadcrumb.map((item, idx) => (
                <span key={idx}>
                  {idx > 0 && <span className="breadcrumb-arrow"> → </span>}
                  <span className={idx === breadcrumb.length - 1 ? 'breadcrumb-current' : 'breadcrumb-parent'}>
                    {item}
                  </span>
                </span>
              ))}
            </span>
          </div>
          <div className="fk-modal-actions">
            <button
              className="fk-modal-btn fk-modal-goto"
              onClick={handleGoToPage}
              title="Open full page"
            >
              <ExternalLink size={16} />
            </button>
            <button
              className="fk-modal-btn fk-modal-close"
              onClick={handleBack}
              title={stack.length > 1 ? 'Back' : 'Close'}
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="fk-modal-body">
          {loading ? (
            <div className="fk-modal-loading">Loading...</div>
          ) : record ? (
            <>
              <div className="detail-grid-24">
                {Object.keys(fieldsByRow)
                  .sort((a, b) => Number(a) - Number(b))
                  .map(rowNum => (
                    <React.Fragment key={rowNum}>
                      {fieldsByRow[rowNum].map(field => renderField(field))}
                    </React.Fragment>
                  ))}
              </div>

              {/* Related Tables */}
              {moduleConfig?.relatedTables?.length > 0 && (
                <div className="related-tables-section fk-modal-related">
                  <div className="detail-section-header">Related Information</div>
                  <div className="related-tables-tabs">
                    {moduleConfig.relatedTables.map((tab, idx) => {
                      const isTabLoading = relatedLoading[tab.tableName];
                      const tabData = relatedData[tab.tableName];
                      const count = tabData?.length || 0;
                      return (
                        <button
                          key={idx}
                          className={`related-tab ${activeRelatedTab === idx ? 'active' : ''} ${isTabLoading ? 'loading' : ''}`}
                          onClick={() => setActiveRelatedTab(idx)}
                        >
                          {tab.label}
                          {isTabLoading ? (
                            <Loader size={12} className="tab-spinner" />
                          ) : count > 0 ? (
                            <span className="related-tab-count">({count})</span>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                  <div className="related-tables-content">
                    {renderRelatedGrid()}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="fk-modal-not-found">Record not found</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FKRecordModal;
